/**
 * Utility functions for generating user-friendly display values
 */

// Garment type prefixes for display
const GARMENT_TYPE_PREFIXES = {
  'jacket': 'J',
  'trouser': 'T',
  'trousers': 'T',
  'waistcoat': 'W',
  'vest': 'V',
  'shirt': 'S',
  'suit': 'SU',
  'coat': 'C',
  'overcoat': 'OC',
  // Test garment types - using TOC for test overcoat as per product prefix
  'test jacket': 'TJ',
  'test_jacket': 'TJ',
  'test trouser': 'TT',
  'test_trouser': 'TT',
  'test waistcoat': 'TW',
  'test_waistcoat': 'TW',
  'test overcoat': 'TOC',
  'test_overcoat': 'TOC',
  'test': 'TS',
};

/**
 * Generate user-friendly garment display number like J1, T1, W1
 * @param {Object} garment - The garment object
 * @param {Array} allGarments - All garments in the same context (order or page)
 * @param {Array} lineItemsWithRefs - Line items with references (optional)
 * @returns {string} User-friendly garment number (e.g., "J1", "T2")
 */
export function getGarmentDisplayNo(garment, allGarments = [], lineItemsWithRefs = []) {
  if (!garment) return '—';
  
  // First try to get from linked line item reference
  if (lineItemsWithRefs?.length > 0) {
    const lineItem = lineItemsWithRefs.find(li => li.line_item_id === garment.line_item_id);
    if (lineItem?.line_item_ref) {
      const refPart = lineItem.line_item_ref.split('-').pop();
      if (refPart) return refPart;
    }
  }
  
  // Generate based on garment type prefix and count
  const type = (garment.garment_type || 'garment').toLowerCase().trim();
  
  // Check for exact match first, then try with underscores/spaces
  let prefix = GARMENT_TYPE_PREFIXES[type];
  if (!prefix) {
    // Try replacing underscores with spaces
    prefix = GARMENT_TYPE_PREFIXES[type.replace(/_/g, ' ')];
  }
  if (!prefix) {
    // Try replacing spaces with underscores
    prefix = GARMENT_TYPE_PREFIXES[type.replace(/ /g, '_')];
  }
  if (!prefix) {
    // Fallback to first 2 chars uppercase
    prefix = type.substring(0, 2).toUpperCase();
  }
  
  // If we have all garments, count same type before this one
  if (allGarments.length > 0) {
    const sameTypeGarments = allGarments.filter(g => {
      const gType = (g.garment_type || '').toLowerCase().trim();
      return gType === type || 
             gType.replace(/_/g, ' ') === type.replace(/_/g, ' ');
    });
    const index = sameTypeGarments.findIndex(g => g.garment_id === garment.garment_id) + 1;
    return `${prefix}${index || 1}`;
  }
  
  // Fallback - just use prefix with 1
  return `${prefix}1`;
}

/**
 * Format customer number for display
 * @param {number|string} customerNo - The customer number
 * @param {string} customerName - Customer name as fallback
 * @returns {string} Formatted customer number (e.g., "C001", "C1234")
 */
export function formatCustomerNo(customerNo, customerName = '') {
  if (customerNo !== null && customerNo !== undefined) {
    return `C${String(customerNo).padStart(3, '0')}`;
  }
  // Fallback to first 8 chars of name
  return customerName ? customerName.substring(0, 8) : '—';
}

/**
 * Extract garment suffix from full garment number
 * Removes the order number prefix and any hyphens to get clean suffix
 * @param {string} garmentNo - Full garment number (e.g., "PER6052UQ-J1" or "PER6052UQJ1")
 * @param {string} orderNumber - Order number (e.g., "PER6052UQ")
 * @returns {string} Garment suffix only (e.g., "J1")
 */
export function extractGarmentSuffix(garmentNo, orderNumber = null) {
  if (!garmentNo) return '—';
  
  // If order number is provided, extract by removing it
  if (orderNumber && garmentNo.startsWith(orderNumber)) {
    let suffix = garmentNo.slice(orderNumber.length);
    // Remove leading hyphen if present (e.g., "-J1" -> "J1")
    suffix = suffix.replace(/^-+/, '');
    return suffix || '—';
  }
  
  // Fallback: Extract last 2-4 characters (typical garment suffix: J1, T2, TJ1, etc.)
  // Match 1-3 letters followed by 1-2 digits at the end
  const match = garmentNo.match(/([A-Z]{1,3}\d{1,2})$/);
  
  if (match) {
    return match[1];
  }
  
  // Last resort: return last 3 characters
  return garmentNo.slice(-3);
}

/**
 * Format garment number with hyphen for display
 * Converts: INT352RBJ1 → INT352RB-J1
 * Requires order number to correctly place the hyphen
 * @param {string} garmentNo - Full garment number without hyphens
 * @param {string} orderNumber - Order number (optional for better accuracy)
 * @returns {string} Formatted with hyphen between order and suffix
 */
export function formatGarmentNoWithHyphen(garmentNo, orderNumber = null) {
  if (!garmentNo) return 'N/A';
  
  // If order number is provided, use it for accurate splitting
  if (orderNumber && garmentNo.startsWith(orderNumber)) {
    const suffix = garmentNo.slice(orderNumber.length);
    return `${orderNumber}-${suffix}`;
  }
  
  // Fallback: Try to extract suffix using regex (1-3 letters + 1-2 digits at end)
  const match = garmentNo.match(/([A-Z]{1,3}\d{1,2})$/);
  
  if (match) {
    const suffix = match[1];
    const orderPart = garmentNo.slice(0, -suffix.length);
    return `${orderPart}-${suffix}`;
  }
  
  // Last resort: return as-is
  return garmentNo;
}

/**
 * Format garment type key into user-friendly label.
 * Examples:
 * - test_jacket -> Test Jacket
 * - overcoat -> Overcoat
 */
export function formatGarmentTypeLabel(garmentType) {
  if (!garmentType) return '—';

  const normalized = String(garmentType)
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase();

  return normalized
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
